import React from 'react';
import { BLANK_DAY, type ShootDay } from '../../utils/clientSetup';

/**
 * THE SHOOT DAYS, and the one-line date field used beside a deliverable.
 *
 * **A job is rarely one day (15 Oct 2026).** ATM was a build day, a tech
 * day and four event days. Asking for a single date was the same mistake
 * as capping episodes at twelve — and `project_shoots` has always been a
 * table, so the limit was only ever in this form.
 *
 * **Where it is shot earns its field** because three things read it: the
 * calendar entry, the call sheet, and the client's dates block. It is the
 * one fact about a shoot that is known at setup and tedious later.
 *
 * The day's name asks **What's happening**, not "What is it" — that label
 * already means the name of a deliverable two screens earlier, and one
 * label cannot mean two things in the same flow.
 *
 * Everything here is optional. A day with no date is dropped on save.
 */

const FIELD = 'mt-2.5 w-full border-0 border-b border-paper/25 bg-transparent pb-2.5 text-[1.05rem] font-light text-paper placeholder:text-paper/20 focus:border-paper focus:outline-none';

/** One date, labelled. Used for a deliverable's due date. */
export function SetupDate({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return <label className="mt-12 block" data-id="element-1274" data-mp="2f8bnpcc6irqz">
      <span className="label block text-paper/40" data-id="element-1275" data-mp="2fb7iwswn72bq">{label}</span>
      <input type="date" value={value} onChange={event => onChange(event.target.value)} className={`${FIELD} w-52`} data-id="element-1276" data-mp="2t7l3iawh3juf" />
    </label>;
}
export function SetupDays({
  days,
  onDays
}: {
  days: ShootDay[];
  onDays: (days: ShootDay[]) => void;
}) {
  const rows = days.length > 0 ? days : [BLANK_DAY];
  const write = (index: number, patch: Partial<ShootDay>) => onDays(rows.map((day, at) => at === index ? {
    ...day,
    ...patch
  } : day));
  return <div className="mt-10 border-l-2 border-paper/30 pl-6" data-id="element-1277" data-mp="371c24o8o1jbd">
      {rows.map((day, index) => <div key={index} className={index > 0 ? 'mt-10 border-t border-ruleDark pt-8' : ''} data-id="element-1278" data-mp="24nxopysw58xv" data-mpi={index}>
          <div className="flex items-baseline justify-between gap-6" data-id="element-1279" data-mp="29n0whr099t9j">
            <p className="label m-0 text-paper/40" data-id="element-1280" data-mp="2dnt7aoos067x">
              {rows.length === 1 ? 'The shoot' : `Day ${index + 1}`}
            </p>
            {rows.length > 1 && <button type="button" onClick={() => onDays(rows.filter((_, at) => at !== index))} className="label-sm text-paper/30 transition-colors duration-200 hover:text-paper" data-id="element-1281" data-mp="2igdydigas57r">
                Remove
              </button>}
          </div>

          <div className="mt-4 flex flex-wrap gap-x-10 gap-y-5" data-id="element-1282" data-mp="31d5an7xf9k2b">
            <label className="block" data-id="element-1283" data-mp="31ccef0w3acyf">
              <span className="label-sm block text-paper/35" data-id="element-1284" data-mp="12xsl0e79i5sg">From</span>
              <input type="date" value={day.date} onChange={event => write(index, {
            date: event.target.value
          })} className={`${FIELD} w-44`} data-id="element-1285" data-mp="12zlx490n9i7u" />
            </label>
            <label className="block" data-id="element-1286" data-mp="uod7onhxru0n">
              <span className="label-sm block text-paper/35" data-id="element-1287" data-mp="unbplz2i679f">To · if it runs</span>
              <input type="date" value={day.end} onChange={event => write(index, {
            end: event.target.value
          })} className={`${FIELD} w-44`} data-id="element-1288" data-mp="pqld1lpwptsr" />
            </label>
          </div>

          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-6" data-id="element-1289" data-mp="1dfmegp0bo4oj">
            <label className="min-w-[12rem] flex-1" data-id="element-1290" data-mp="1dg5uu1abql0g">
              <span className="label-sm block text-paper/35" data-id="element-1291" data-mp="19czv7chpvs2y">What's happening</span>
              <input value={day.title} onChange={event => write(index, {
            title: event.target.value
          })} className={FIELD} data-id="element-1292" data-mp="14ey9ejswpens" />
            </label>
            <label className="min-w-[12rem] flex-1" data-id="element-1293" data-mp="3p4dwp4sx2l8o">
              <span className="label-sm block text-paper/35" data-id="element-1294" data-mp="3p3m482um93mm">Where</span>
              <input value={day.location} onChange={event => write(index, {
            location: event.target.value
          })} className={FIELD} data-id="element-1295" data-mp="1gvfk9u1mnml2" />
            </label>
          </div>
        </div>)}

      <button type="button" onClick={() => onDays([...rows, {
      ...BLANK_DAY
    }])} className="label mt-9 text-paper/45 transition-colors duration-200 hover:text-paper" data-id="element-1296" data-mp="1bx4afjbc1c2u">
        + Another day
      </button>
    </div>;
}